/**
 * NewScreeningPage — Package-first intake for NTN Verify Hub.
 *
 * ▸ Primary path: "Submit Completed Application Package" — Property Manager
 *   already has a signed rental application PDF + supporting docs. Fills a
 *   tiny intake, uploads the package, picks services, submits.
 * ▸ Secondary path: "Send Application Link" — for the 5% case where PM wants
 *   the applicant to fill our digital rental application via email link.
 *   Both intake methods merge into the same NTN Operations workflow.
 * ▸ "Fill on behalf" has been removed — it created a second operational
 *   workflow that conflicts with the platform's single-processing-path
 *   architecture.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, REPORT_TYPES, DECISION_READY_PACKAGE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ServiceOrderPanel from "@/components/ServiceOrderPanel";
import { SingleFileField, MultiFileField, uploadFileToScreening } from "@/components/FileUploadFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, Mail, Check, Plus, X, Building2,
  Package, Link as LinkIcon, ShieldCheck,
} from "lucide-react";

const DEFAULT_SERVICES = [...DECISION_READY_PACKAGE];
const DEFAULT_PACKAGE = "decision_ready";
const ROLE_LABEL = { primary: "Primary", co_applicant: "Co-applicant", cosigner: "Cosigner" };

export default function NewScreeningPage() {
  const { user, companyLabel } = useAuth();
  const isStaff = user?.role === "ntn_staff";
  const [mode, setMode] = useState("package"); // "package" | "link"
  const [billToCompanyId, setBillToCompanyId] = useState("");

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto ntn-fade-up">
        <div className="font-overline text-[#4A5568] mb-3">New Screening Request</div>
        {companyLabel && (
          <div className="text-sm text-[#4A5568] mb-1" data-testid="new-request-company">
            Ordering under <span className="font-semibold text-[#0A192F]">{companyLabel}</span>
          </div>
        )}
        <h1 className="font-serif text-4xl sm:text-5xl text-[#0A192F] tracking-tight mb-3" data-testid="new-request-heading">
          Send NTN the package<span className="text-[#F59E0B]">.</span>
        </h1>
        <p className="text-[#4A5568] mb-8 max-w-2xl">
          You already have the applicant&apos;s rental application and supporting documents.
          Upload the package, pick the services you want NTN to run, and we&apos;ll return a
          Decision-Ready Application.
        </p>

        {isStaff && (
          <BillToClientPicker value={billToCompanyId} onChange={setBillToCompanyId} />
        )}

        {/* Intake mode selector — both paths merge into the same Ops workflow. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setMode("package")}
            className={`text-left p-4 border rounded-sm transition-all ${
              mode === "package"
                ? "border-[#0A192F] bg-[#0A192F] text-white"
                : "bg-white border-[#0A192F]/15 hover:border-[#0A192F]/40"
            }`}
            data-testid="mode-package"
          >
            <div className="flex items-start justify-between mb-2">
              <Package className="h-5 w-5" strokeWidth={1.5} />
              {mode === "package" && <Check className="h-4 w-4" />}
            </div>
            <div className="font-serif text-base">Submit completed package</div>
            <div className={`text-xs mt-1 ${mode === "package" ? "text-white/70" : "text-[#4A5568]"}`}>
              Primary. Upload the applicant&apos;s rental application PDF + supporting docs.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("link")}
            className={`text-left p-4 border rounded-sm transition-all ${
              mode === "link"
                ? "border-[#0A192F] bg-[#0A192F] text-white"
                : "bg-white border-[#0A192F]/15 hover:border-[#0A192F]/40"
            }`}
            data-testid="mode-link"
          >
            <div className="flex items-start justify-between mb-2">
              <LinkIcon className="h-5 w-5" strokeWidth={1.5} />
              {mode === "link" && <Check className="h-4 w-4" />}
            </div>
            <div className="font-serif text-base">Send application link</div>
            <div className={`text-xs mt-1 ${mode === "link" ? "text-white/70" : "text-[#4A5568]"}`}>
              Secondary. Email your rental application link. Results merge into the same NTN workflow.
            </div>
          </button>
        </div>

        {mode === "package" && (
          <PackageForm billToCompanyId={isStaff ? billToCompanyId : null} />
        )}
        {mode === "link" && <LinkForm />}
      </div>
    </AppShell>
  );
}

/* --------------------------------------------------------------------- */
/* Primary — Submit Completed Package                                    */
/* --------------------------------------------------------------------- */
function PackageForm({ billToCompanyId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowedProducts = user?.company_allowed_products;
  // Phase E.2: preselect the company's standard package, falling back to
  // the platform-wide Decision-Ready bundle if the company has no
  // configured defaults (typically an NTN staff-side order).
  const initialDefault = (user?.company_default_products?.length
    ? user.company_default_products
    : DEFAULT_SERVICES
  ).filter((k) => !allowedProducts || allowedProducts.includes(k));
  const [primary, setPrimary] = useState({
    applicant_first_name: "", applicant_last_name: "",
    applicant_email: "", applicant_phone: "",
  });
  const [property, setProperty] = useState({ property_address: "", monthly_rent: "" });
  const [services, setServices] = useState(initialDefault);
  // The `decision_ready` package marker only applies when every one of
  // the 6 platform bundle services is selected — which we can only offer
  // when they're ALL allowed. Otherwise start with no package marker.
  const canBundle = !allowedProducts || DECISION_READY_PACKAGE.every((k) => allowedProducts.includes(k));
  const [pkg, setPkg] = useState(canBundle && initialDefault.length === DECISION_READY_PACKAGE.length ? DEFAULT_PACKAGE : null);
  const [additional, setAdditional] = useState([]); // {role, first, last, email, phone}
  const [rentalAppPdf, setRentalAppPdf] = useState(null); // single File
  const [supportingDocs, setSupportingDocs] = useState([]); // File[]
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(""); // status line during multi-step submit

  const addMember = (role) => setAdditional((a) => [...a, {
    role, applicant_first_name: "", applicant_last_name: "", applicant_email: "", applicant_phone: "",
    rentalAppPdf: null, docs: [],
  }]);
  const setMember = (i, k, v) => setAdditional((a) => a.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const removeMember = (i) => setAdditional((a) => a.filter((_, idx) => idx !== i));

  const isGroup = additional.length > 0;
  const valid =
    primary.applicant_first_name &&
    primary.applicant_last_name &&
    primary.applicant_email &&
    property.property_address &&
    services.length > 0 &&
    !!rentalAppPdf &&
    (!isGroup || additional.every((m) => m.applicant_first_name && m.applicant_last_name && m.applicant_email));

  const submit = async () => {
    setSubmitting(true); setError(""); setProgress("");
    try {
      // 1) Create the screening record (single or group).
      let primaryScreeningId;
      let groupId;
      let memberScreenings = [];
      if (isGroup) {
        setProgress("Creating household…");
        const payload = {
          property_address: property.property_address,
          monthly_rent: property.monthly_rent ? parseFloat(property.monthly_rent) : null,
          services, package: pkg,
          members: [
            { role: "primary", ...primary },
            ...additional.map((m) => ({
              role: m.role,
              applicant_first_name: m.applicant_first_name,
              applicant_last_name: m.applicant_last_name,
              applicant_email: m.applicant_email,
              applicant_phone: m.applicant_phone || null,
            })),
          ],
          bill_to_company_id: billToCompanyId || null,
        };
        const { data } = await apiClient.post("/groups", payload);
        groupId = data.id;
        memberScreenings = data.member_screenings || [];
        const primaryMs = memberScreenings.find((ms) => ms.role === "primary");
        primaryScreeningId = primaryMs?.screening_id;
      } else {
        setProgress("Creating screening…");
        const payload = {
          applicant_first_name: primary.applicant_first_name,
          applicant_last_name: primary.applicant_last_name,
          applicant_email: primary.applicant_email,
          applicant_phone: primary.applicant_phone || null,
          property_address: property.property_address,
          monthly_rent: property.monthly_rent ? parseFloat(property.monthly_rent) : null,
          services, package: pkg,
          custom_message: notes || null,
          bill_to_company_id: billToCompanyId || null,
        };
        const { data } = await apiClient.post("/screenings/quick", payload);
        primaryScreeningId = data.id;
      }
      if (!primaryScreeningId) throw new Error("Could not resolve primary screening");

      // 2) Upload the primary's Rental Application PDF (source-of-truth file).
      setProgress("Uploading rental application…");
      const rr = await uploadFileToScreening(primaryScreeningId, rentalAppPdf, "rental_application");
      if (!rr.ok) throw new Error(`Rental application upload failed: ${rr.error}`);

      // 3) Upload the primary's supporting documents.
      if (supportingDocs.length > 0) {
        for (let i = 0; i < supportingDocs.length; i++) {
          setProgress(`Uploading supporting docs (${i + 1}/${supportingDocs.length})…`);
          const r = await uploadFileToScreening(primaryScreeningId, supportingDocs[i], "supporting_document");
          if (!r.ok) toast.error(`${supportingDocs[i].name} — ${r.error}`);
        }
      }

      // v1.0.7 — per-member uploads: each member's files attach to THEIR own
      // screening. Match by role+email, consuming each created screening once
      // so duplicate emails can never cross-attach files.
      if (isGroup && memberScreenings.length > 0) {
        const remaining = memberScreenings.filter((ms) => ms.screening_id !== primaryScreeningId);
        const takeScreeningFor = (m) => {
          const email = (m.applicant_email || "").toLowerCase();
          let idx = remaining.findIndex(
            (ms) => ms.role === m.role && (ms.applicant_email || "").toLowerCase() === email,
          );
          if (idx === -1) idx = remaining.findIndex((ms) => (ms.applicant_email || "").toLowerCase() === email);
          if (idx === -1) return null;
          return remaining.splice(idx, 1)[0].screening_id;
        };
        for (const m of additional) {
          const sid = takeScreeningFor(m);
          if (!sid) continue;
          const label = `${m.applicant_first_name} ${m.applicant_last_name}`.trim();
          if (m.rentalAppPdf) {
            setProgress(`Uploading ${label}'s rental application…`);
            const r = await uploadFileToScreening(sid, m.rentalAppPdf, "rental_application");
            if (!r.ok) toast.error(`${m.rentalAppPdf.name} — ${r.error}`);
          }
          for (let i = 0; i < (m.docs || []).length; i++) {
            setProgress(`Uploading ${label}'s documents (${i + 1}/${m.docs.length})…`);
            const r = await uploadFileToScreening(sid, m.docs[i], "supporting_document");
            if (!r.ok) toast.error(`${m.docs[i].name} — ${r.error}`);
          }
        }
      }

      toast.success("Package submitted to NTN Operations");
      navigate(isGroup ? `/groups/${groupId}` : `/screenings/${primaryScreeningId}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
      setProgress("");
    }
  };

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8 space-y-6 ntn-fade-up" data-testid="package-form">
      {/* Applicant */}
      <section>
        <h2 className="font-serif text-xl text-[#0A192F] mb-1">Applicant</h2>
        <p className="text-xs text-[#4A5568] mb-4">Just enough for NTN to route the file.</p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name" required>
            <Input value={primary.applicant_first_name}
                   onChange={(e) => setPrimary({ ...primary, applicant_first_name: e.target.value })}
                   className="h-11 rounded-sm" data-testid="pkg-first-name" />
          </FormField>
          <FormField label="Last name" required>
            <Input value={primary.applicant_last_name}
                   onChange={(e) => setPrimary({ ...primary, applicant_last_name: e.target.value })}
                   className="h-11 rounded-sm" data-testid="pkg-last-name" />
          </FormField>
          <FormField label="Email" required>
            <Input type="email" value={primary.applicant_email}
                   onChange={(e) => setPrimary({ ...primary, applicant_email: e.target.value })}
                   className="h-11 rounded-sm" data-testid="pkg-email" />
          </FormField>
          <FormField label="Phone">
            <Input value={primary.applicant_phone}
                   onChange={(e) => setPrimary({ ...primary, applicant_phone: e.target.value })}
                   className="h-11 rounded-sm" data-testid="pkg-phone" />
          </FormField>
        </div>
      </section>

      {/* Property */}
      <section className="border-t border-[#0A192F]/10 pt-5">
        <h2 className="font-serif text-xl text-[#0A192F] mb-4">Property</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <FormField label="Address" required>
              <Input value={property.property_address}
                     onChange={(e) => setProperty({ ...property, property_address: e.target.value })}
                     className="h-11 rounded-sm" data-testid="pkg-address" />
            </FormField>
          </div>
          <FormField label="Monthly rent (USD)">
            <Input type="number" value={property.monthly_rent}
                   onChange={(e) => setProperty({ ...property, monthly_rent: e.target.value })}
                   className="h-11 rounded-sm" data-testid="pkg-rent" />
          </FormField>
        </div>
      </section>

      {/* Household (optional) */}
      <section className="border-t border-[#0A192F]/10 pt-5">
        <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
          <div>
            <h2 className="font-serif text-xl text-[#0A192F]">Household Members <span className="text-sm text-[#4A5568] font-sans">(optional)</span></h2>
            <p className="text-xs text-[#4A5568]">Add co-applicants or cosigners to screen as one household.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addMember("co_applicant")}
                    className="rounded-sm h-9" data-testid="pkg-add-coapp">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Co-applicant
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addMember("cosigner")}
                    className="rounded-sm h-9" data-testid="pkg-add-cosigner">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Cosigner
            </Button>
          </div>
        </div>

        {additional.length > 0 && (
          <div className="space-y-3 mt-4">
            {additional.map((m, i) => (
              <div key={i} className="border border-[#0A192F]/12 rounded-sm p-4" data-testid={`pkg-member-${i}`}>
                <div className="flex items-center justify-between mb-3 gap-3">
                  <span className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#0A192F] bg-[#F3EFEA] px-2 py-1 rounded-sm">
                    {ROLE_LABEL[m.role]}
                  </span>
                  <button type="button" onClick={() => removeMember(i)}
                          className="h-7 w-7 rounded-sm flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/10"
                          data-testid={`pkg-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={m.applicant_first_name} onChange={(e) => setMember(i, "applicant_first_name", e.target.value)}
                         placeholder="First name" className="h-11 rounded-sm" data-testid={`pkg-mfname-${i}`} />
                  <Input value={m.applicant_last_name} onChange={(e) => setMember(i, "applicant_last_name", e.target.value)}
                         placeholder="Last name" className="h-11 rounded-sm" data-testid={`pkg-mlname-${i}`} />
                  <Input type="email" value={m.applicant_email} onChange={(e) => setMember(i, "applicant_email", e.target.value)}
                         placeholder="Email" className="h-11 rounded-sm" data-testid={`pkg-memail-${i}`} />
                  <Input value={m.applicant_phone} onChange={(e) => setMember(i, "applicant_phone", e.target.value)}
                         placeholder="Phone" className="h-11 rounded-sm" data-testid={`pkg-mphone-${i}`} />
                </div>
                {/* v1.0.7 — per-member documents attach to THIS member's own screening. */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-[#0A192F]/8 pt-4">
                  <SingleFileField
                    file={m.rentalAppPdf}
                    onChange={(f) => setMember(i, "rentalAppPdf", f)}
                    testidPrefix={`pkg-mrental-${i}`}
                    title="Rental application (optional)"
                    compact
                  />
                  <MultiFileField
                    files={m.docs || []}
                    onChange={(fs) => setMember(i, "docs", fs)}
                    testidPrefix={`pkg-mdocs-${i}`}
                    title="Supporting documents"
                    compact
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Application package — required PDF + supporting docs */}
      <section className="border-t border-[#0A192F]/10 pt-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-sm bg-[#0A192F]/6 flex items-center justify-center shrink-0">
            <Package className="h-5 w-5 text-[#0A192F]" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-serif text-xl text-[#0A192F]">Application Package</h2>
            <p className="text-xs text-[#4A5568]">
              The rental application PDF becomes NTN&apos;s working file. Supporting documents attach for verification.
            </p>
          </div>
        </div>

        <SingleFileField
          file={rentalAppPdf}
          onChange={setRentalAppPdf}
          testidPrefix="pkg-rental-app"
          title="Rental application PDF"
          hint="Signed rental application. PDF preferred, up to 25 MB."
          required
        />

        <div className="mt-4">
          <MultiFileField
            files={supportingDocs}
            onChange={setSupportingDocs}
            testidPrefix="pkg-supporting"
            title="Supporting documents"
            hint="Paystubs, bank statements, IDs, W-2s, or anything else relevant to verification."
          />
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#0A192F]/10 pt-5">
        <h2 className="font-serif text-xl text-[#0A192F] mb-1">Services to Order</h2>
        <p className="text-xs text-[#4A5568] mb-4">
          NTN only performs the services you pick here — this is the blueprint for our processing workflow.
        </p>
        <ServiceOrderPanel
          services={services}
          onChange={setServices}
          pkg={pkg}
          onPackageChange={setPkg}
          allowedProducts={allowedProducts}
          testidPrefix="pkg"
        />
      </section>

      {/* Notes */}
      <section className="border-t border-[#0A192F]/10 pt-5">
        <FormField label="Notes for NTN staff">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="rounded-sm" data-testid="pkg-notes" />
        </FormField>
      </section>

      {error && <div className="text-sm text-[#EF4444]" data-testid="pkg-error">{error}</div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#0A192F]/10">
        <div className="flex items-center gap-2 text-xs text-[#4A5568]">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          The uploaded package is encrypted at rest and becomes NTN&apos;s source of truth.
        </div>
        <Button onClick={submit} disabled={!valid || submitting}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-6"
                data-testid="pkg-submit">
          {submitting
            ? (progress || "Submitting…")
            : <>Submit package to NTN <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Secondary — Send Application Link                                     */
/* --------------------------------------------------------------------- */
function LinkForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", property_address: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const valid = form.first_name && form.last_name && form.property_address && form.email;

  const submit = async () => {
    setBusy(true); setError(""); setOk(false);
    try {
      await apiClient.post("/outreach/send", form);
      setOk(true);
      setForm({ first_name: "", last_name: "", property_address: "", email: "" });
      toast.success("Application link emailed");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8 space-y-5 ntn-fade-up" data-testid="link-form">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#0A192F]/6 flex items-center justify-center shrink-0">
          <Mail className="h-5 w-5 text-[#0A192F]" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-serif text-xl text-[#0A192F]">Send the applicant a rental application link</h2>
          <p className="text-xs text-[#4A5568] mt-1">
            The applicant fills your application online. Once submitted, the completed package flows
            into the same NTN Operations workflow as any uploaded package.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="First name" required>
          <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                 className="h-11 rounded-sm" data-testid="link-first-name" />
        </FormField>
        <FormField label="Last name" required>
          <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                 className="h-11 rounded-sm" data-testid="link-last-name" />
        </FormField>
        <div className="col-span-2">
          <FormField label="Property address" required>
            <Input value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                   className="h-11 rounded-sm" data-testid="link-address" />
          </FormField>
        </div>
        <div className="col-span-2">
          <FormField label="Applicant email" required>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="h-11 rounded-sm" data-testid="link-email" />
          </FormField>
        </div>
      </div>

      {error && <div className="text-sm text-[#EF4444]" data-testid="link-error">{error}</div>}
      {ok && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="link-success">
          Application link sent. You&apos;ll see the submitted package in the queue once the applicant completes it.
        </div>
      )}

      <div className="flex justify-end pt-3 border-t border-[#0A192F]/10">
        <Button onClick={submit} disabled={!valid || busy}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-6"
                data-testid="link-submit">
          {busy ? "Sending…" : <><Mail className="h-4 w-4 mr-2" /> Send application link</>}
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Reusable form elements                                                */
/* --------------------------------------------------------------------- */
function FormField({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-1.5 block">
        {label}{required && <span className="text-[#EF4444] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Bill-to-client picker (staff only)                                    */
/* --------------------------------------------------------------------- */
function BillToClientPicker({ value, onChange }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    apiClient.get("/admin/clients")
      .then(({ data }) => { if (mounted) setClients((data || []).filter((c) => !c.suspended_at)); })
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  const selected = clients.find((c) => c.id === value);
  return (
    <div className="mb-6 border border-[#0A192F]/15 bg-[#F3EFEA]/50 rounded-sm p-4 ntn-fade-up" data-testid="bill-to-client-picker">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-sm bg-white border border-[#0A192F]/10 flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 text-[#0A192F]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-overline text-[#4A5568] mb-1">Bill to client (optional)</div>
          <p className="text-xs text-[#4A5568] mb-3">
            Place this order on a client&apos;s behalf. Leave blank to keep ownership with NTN.
          </p>
          <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
            <SelectTrigger className="h-11 rounded-sm bg-white max-w-md" data-testid="bill-to-client-select">
              <SelectValue placeholder={loading ? "Loading clients…" : "Keep ownership with NTN"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Keep ownership with NTN</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`bill-to-client-option-${c.id}`}>
                  {c.name}{c.account_number ? ` · #${c.account_number}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected && (
            <p className="text-[11px] text-[#4A5568] font-mono mt-2" data-testid="bill-to-client-selected">
              ORDER WILL APPEAR IN {selected.name.toUpperCase()}&apos;S DASHBOARD
              {selected.account_number ? ` · ACCT #${selected.account_number}` : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
